// Which paths the SPA answers, and which of them search engines may index.
//
// Shared by the router (noindex on private areas, src/routes/index.jsx),
// the Vercel Routing Middleware (real 404 status for unknown paths,
// middleware.js) and the robots.txt test. Pure: no DOM, no React, so it
// also runs on the edge. When a route is added to src/routes/index.jsx,
// add its path here too; routes.test.js fails until both agree.

import { CONTENT_PATHS } from './contentPaths.js'

export const SITE_ORIGIN = 'https://myrentra.com'

/** Every route path the router declares (react-router syntax). */
export const ROUTE_PATHS = [
  '/',
  '/login',
  '/register',
  '/terms',
  '/privacy',
  '/legal',
  '/legal/esign',
  '/legal/screening',
  '/legal/fees',
  '/legal/tenant-rights',
  '/legal/community',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/cosigner/accept/:token',
  '/tenant-invite/:token',
  '/lead/:token',
  '/household/:token',
  '/housemates/waitlist/stop/:token',
  // The waitlist's "your cohort is open" link (public, noindexed).
  '/housemates/join/:token',
  // The public cohort page (indexable: see INDEXABLE_PATTERNS).
  '/housemates/:city/:area/:month',
  '/pay/:token/:method',
  '/landlord-invite/:token',
  '/guaranty/:token',
  '/bank-authorization/:token',
  '/showings/cancel/:token',
  '/team-invite/:token',
  '/listings',
  '/cosigner',
  '/cosigner/rental-application',
  '/favorites',
  '/listings/:id',
  '/messages',
  '/messages/:conversationId',
  '/housemates',
  '/housemates/list-group',
  '/housemates/me',
  '/housemates/start',
  '/housemates/:id',
  '/profile',
  '/profile/screening',
  '/profile/screening/get',
  '/screening/consent/:applicationId',
  '/profile/edit',
  '/profile/tenant-dashboard',
  '/payments',
  '/pre-qualify',
  '/applications',
  '/applications/rental-application',
  '/apply/:listingId',
  '/agreement/:agreementId',
  '/inspections/:id/edit',
  '/inspections/:id',
  '/roommates',
  '/roommates/matching',
  '/groups',
  '/groups/create',
  '/groups/:id',
  '/groups/:id/chat',
  '/households/:id',
  '/households/:id/chat',
  '/dashboard',
  '/calendar/google/callback',
  '/dashboard/inbox',
  '/dashboard/listings/new',
  '/dashboard/listings/:id/edit',
  '/dashboard/properties/new',
  '/dashboard/properties/:id',
  '/dashboard/properties/:id/onboard',
  '/dashboard/properties/:id/inspections/:inspectionId',
  '/dashboard/properties/:id/:tab',
  '/dashboard/import',
  '/dashboard/rent-collection',
  '/dashboard/security-deposits',
  '/dashboard/payments-in',
  '/dashboard/banking',
  '/dashboard/tax',
  '/dashboard/documents',
  '/dashboard/team',
  '/dashboard/reports',
  '/dashboard/showings',
  '/dashboard/automation',
  '/dashboard/vendors',
  '/dashboard/inspections',
  '/dashboard/lease-templates',
  '/dashboard/lease-templates/:id',
  '/dashboard/screening/:applicationId',
  '/dashboard/applications/:id/application',
  '/dashboard/approvals',
  '/dashboard/disputes',
  // Public content pages: neighborhoods, off-campus pages, guides.
  ...CONTENT_PATHS,
]

/**
 * Path prefixes that are never indexed: signed-in app areas, links that
 * carry a token from an email, and the sign-in pages. A prefix matches the
 * path itself and anything under it ("/profile" covers "/profile/edit").
 * robots.txt blocks crawling of the private areas; the sign-in pages stay
 * crawlable so the noindex tag is seen.
 */
export const NOINDEX_PREFIXES = [
  '/dashboard',
  '/profile',
  '/applications',
  '/apply',
  '/messages',
  '/screening',
  '/inspections',
  '/agreement',
  '/favorites',
  '/payments',
  '/pre-qualify',
  '/housemates',
  '/groups',
  '/households',
  // A household invite link: noindexed, but left crawlable in robots.txt
  // so link-preview bots that honour robots.txt can read its card.
  '/household',
  '/roommates',
  '/cosigner',
  '/calendar',
  '/tenant-invite',
  '/lead',
  '/landlord-invite',
  '/team-invite',
  '/guaranty',
  // A bank account holder's own debit authorization link.
  '/bank-authorization',
  '/showings',
  // Short pay links in shared-bill notices.
  '/pay',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
]

/**
 * Public pages under a noindex prefix that search engines may index: the
 * Housemates cohort pages (/housemates/<city>/<area>/<YYYY-MM>, which show
 * counts and only opted-in cards). Mirrored by an Allow line in robots.txt
 * and by the separate /housemates X-Robots-Tag rule in vercel.json.
 */
export const INDEXABLE_PATTERNS = [
  /^\/housemates\/[a-z0-9-]+\/[a-z0-9-]+\/\d{4}-\d{2}$/,
]

const escapeRegex = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** '/listings/:id' -> /^\/listings\/[^/]+$/ */
export function patternFor(routePath) {
  const body = routePath
    .split('/')
    .map(seg => (seg.startsWith(':') ? '[^/]+' : escapeRegex(seg)))
    .join('/')
  return new RegExp(`^${body || '/'}$`)
}

// Compiled on first use, so the app shell (which only asks isNoindexPath)
// does not carry the route list.
let routePatterns = null

/** '/listings/' -> '/listings'; the root stays '/'. Query and hash dropped. */
export function normalizePath(pathname = '/') {
  const path = String(pathname).split(/[?#]/)[0] || '/'
  return path.length > 1 ? path.replace(/\/+$/, '') || '/' : path
}

/** True when the router has a page for this path. */
export function isKnownPath(pathname) {
  const path = normalizePath(pathname)
  if (!routePatterns) routePatterns = ROUTE_PATHS.map(patternFor)
  return routePatterns.some(re => re.test(path))
}

/** True when this path must carry `noindex`. */
export function isNoindexPath(pathname) {
  const path = normalizePath(pathname)
  if (INDEXABLE_PATTERNS.some(re => re.test(path))) return false
  return NOINDEX_PREFIXES.some(
    prefix => path === prefix || path.startsWith(`${prefix}/`)
  )
}

/** The listing id in '/listings/:id', else null. */
export function listingIdFromPath(pathname) {
  const match = /^\/listings\/([A-Za-z0-9_-]{1,64})$/.exec(
    normalizePath(pathname)
  )
  return match ? match[1] : null
}

/** Canonical URL for a path: the site origin plus the path, no query. */
export function canonicalUrl(pathname) {
  return `${SITE_ORIGIN}${normalizePath(pathname)}`
}
